// splatCache — persistent client-side cache for Gaussian-splat environment
// binaries (often 100MB+).
//
// This is a thin instance of the shared Cache-Storage factory in assetCache.js.
// Splats are unbounded (one or two per character, each huge — an entry cap would
// thrash) and consumed as blob: URLs by Spark, so this instance keeps maxEntries
// at Infinity and callers use matchBlobUrl. The factory carries the full
// rationale (why not the HTTP cache, versioning, best-effort semantics).
//
// The version param stays '__splatver' for back-compat with existing cached
// entries and the keyFor/baseOf unit tests.

import { createAssetCache } from './assetCache.js'
import { plyTruncationReason, PLY_HEADER_SCAN_BYTES } from './plyIntegrity.js'

// Read-side integrity: a binary PLY's header declares its own byte length, so
// a truncated cached copy (a download an intermediary cut cleanly — the route
// streams chunked with no Content-Length, so fetch() can't tell) is detectable
// from a small header slice + blob.size, without reading the multi-100MB body.
// Rejecting it here evicts the entry and reports a miss, so the next load
// re-downloads instead of replaying the poison into Spark's decode worker
// forever. Non-PLY formats (spz/splat/ksplat) return null — cannot verify,
// never reject. The deliberately-unbumped bucket name keeps everyone's good
// entries; this check is what heals the bad ones.
async function verifySplatBlob(blob) {
  const head = new Uint8Array(await blob.slice(0, PLY_HEADER_SCAN_BYTES).arrayBuffer())
  return plyTruncationReason(head, blob.size)
}

const splatCache = createAssetCache({
  cacheName: 'aos-splat-v1',
  verParam: '__splatver',
  maxEntries: Infinity,
  label: 'splatCache',
  verifyBlob: verifySplatBlob,
})

// Named re-exports so `import * as splatCache` and `import { keyFor }` keep working.
export const CACHE_NAME = splatCache.CACHE_NAME
export const keyFor = splatCache.keyFor
export const baseOf = splatCache.baseOf
export const matchBlobUrl = splatCache.matchBlobUrl
export const matchArrayBuffer = splatCache.matchArrayBuffer
export const store = splatCache.store
export const evict = splatCache.evict
export const clearAll = splatCache.clearAll

if (typeof window !== 'undefined') {
  window.__SPLAT_CACHE__ = { matchBlobUrl, store, evict, clearAll, CACHE_NAME }
}
